import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AgentCreationLogsService } from "@/services/agent/AgentCreationLogsService";
import type { AgentCreationLogFilters, RestartAgentCreationPayload } from "@/types/agent-creation-logs";

export const useAgentCreationLogs = (
  filters?: AgentCreationLogFilters,
  page: number = 1,
  pageSize: number = 50,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["agent-creation-logs", filters, page, pageSize],
    queryFn: () => AgentCreationLogsService.getAgentCreationLogs(filters, pageSize, (page - 1) * pageSize),
    staleTime: 1000 * 60,
    refetchInterval: enabled ? 1000 * 60 : false,
    enabled,
  });
};

export const useAgentCreationLog = (id: number) => {
  return useQuery({
    queryKey: ["agent-creation-log", id],
    queryFn: () => AgentCreationLogsService.getAgentCreationLog(id),
    enabled: !!id,
  });
};

export const useAgentCreationLogDetails = (logId: number) => {
  return useQuery({
    queryKey: ["agent-creation-log-details", logId],
    queryFn: () => AgentCreationLogsService.getAgentCreationLogDetails(logId),
    staleTime: 0,
    refetchInterval: 1000 * 60,
    refetchOnMount: true,
    enabled: !!logId,
  });
};

export const useAgentCreationLogStats = (filters?: AgentCreationLogFilters, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["agent-creation-log-stats", filters],
    queryFn: () => AgentCreationLogsService.getAgentCreationLogStats(filters),
    staleTime: 1000 * 60,
    refetchInterval: enabled ? 1000 * 60 : false,
    enabled,
  });
};

export const useRestartAgentCreation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RestartAgentCreationPayload) => AgentCreationLogsService.restartAgentCreation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-creation-logs"] });
      queryClient.invalidateQueries({ queryKey: ["agent-creation-log-stats"] });
    },
  });
};
